import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { formatMoney, isNegativeMoney } from '../lib/money'
import { formatPercent } from '../lib/overview'
import type { CostOverview, CostOverviewTotals } from '../types'

const COLUMNS: { key: keyof CostOverviewTotals; label: string }[] = [
  { key: 'revisedBudget', label: 'CostControl_col_revisedBudget' },
  { key: 'committed', label: 'CostControl_col_committed' },
  { key: 'actual', label: 'CostControl_col_actual' },
  { key: 'projectedFinalCost', label: 'CostControl_col_projectedFinalCost' },
  { key: 'variance', label: 'CostControl_col_variance' },
]

interface Props { view: CostOverview; showScopes: boolean; locale: string; onOpenPending: () => void }

const label = (r: { code: string | null; name: string }) => (r.code ? `${r.code} · ${r.name}` : r.name)

/** 1b-C spec §4: the buildings (whole project only), the largest overruns, and what waits for a decision. */
const OverviewTables: React.FC<Props> = ({ view, showScopes, locale, onOpenPending }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency: view.currency, whole: true })
  const p = view.pending
  const counts = [
    { key: 'CostControl_pending_type_APPLICATION', n: p.applications },
    { key: 'CostControl_pending_type_CHANGE', n: p.changes },
    { key: 'CostControl_pending_type_INVOICE', n: p.invoices },
  ]
  return (
    <div className="space-y-4">
      {showScopes && view.scopes.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('CostControl_overview_buildings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('CostControl_col_name')}</TableHead>
                  {COLUMNS.map((c) => <TableHead key={c.key} className="text-right">{t(c.label)}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.scopes.map((s) => (
                  <TableRow key={s.nodeId}>
                    <TableCell>{label(s)}</TableCell>
                    {COLUMNS.map((c) => (
                      <TableCell key={c.key} className={cn('text-right tabular-nums', c.key === 'variance' && isNegativeMoney(s.totals.variance) && 'text-destructive')}>
                        {money(s.totals[c.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('CostControl_overview_overruns')}</CardTitle>
          </CardHeader>
          <CardContent>
            {view.overruns.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('CostControl_overview_overruns_empty')}</p>
            ) : (
              <ul className="divide-y">
                {view.overruns.map((o) => (
                  <li key={o.nodeId} className="flex items-baseline justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0 truncate">{label(o)}</span>
                    <span className="shrink-0 tabular-nums text-destructive">
                      +{money(o.overrun)}{o.overrunPercent !== null && ` (${formatPercent(o.overrunPercent, locale)} %)`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('CostControl_overview_pending')}</CardTitle>
            <Button variant="ghost" size="sm" className="h-7" onClick={onOpenPending}>
              {t('CostControl_overview_pending_open')}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <dl className="space-y-1 text-sm">
              {counts.map((c) => (
                <div key={c.key} className="flex justify-between">
                  <dt className="text-muted-foreground">{t(c.key)}</dt>
                  <dd className="tabular-nums">{c.n}</dd>
                </div>
              ))}
            </dl>
            {p.overdueInvoices > 0 && <p className="mt-2 text-xs text-destructive">{t('CostControl_overview_pending_overdue', { count: p.overdueInvoices })}</p>}
            {p.oldestDays !== null && <p className="mt-1 text-xs text-muted-foreground">{t('CostControl_overview_pending_oldest', { days: p.oldestDays })}</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
export default OverviewTables
